import type { ReactNode } from "react";
import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";

/**
 * Ayuda de «Generar paquete ZIP» (`components/GenerarPaqueteModal`): lo que
 * antes iba en los tooltips de sus fichas y del botón «Solo del Mensaje al
 * cliente». Las fichas se pintan con las mismas clases que en la lista; si
 * cambian allá, cámbialas aquí.
 */

function Ficha({ cls, children }: { cls: string; children: ReactNode }) {
  return (
    <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] uppercase ${cls}`}>
      {children}
    </span>
  );
}

export function ayudaGenerarPaquete({
  tieneTagCliente,
}: {
  /** El caso tiene imágenes subidas desde el editor de un correo: sale el
   *  botón «Solo del Mensaje al cliente». */
  tieneTagCliente: boolean;
}): ContenidoAyuda {
  const botones: ItemAyuda[] = [
    {
      label: "Asignar paquete a IMEI",
      text: "Solo cambia el nombre del ZIP (MODELO_IMEI.zip) para que el proveedor sepa de qué equipo es; la contraseña no cambia. Elegir un IMEI vuelve a marcar las evidencias de ese equipo y las que no tienen equipo, menos las internas.",
    },
    {
      label: "Sin contraseña",
      text: "Arma el ZIP sin contraseña: del otro lado solo abren el enlace y descargan.",
    },
  ];
  if (tieneTagCliente) {
    botones.push({
      label: "Solo del Mensaje al cliente",
      text: "Marca solo las imágenes que se subieron desde el editor de un correo y desmarca todo lo demás.",
    });
  }
  botones.push(
    {
      label: "Todos",
      text: "Marca todos los archivos del caso, también los internos (bitácora y del proveedor).",
    },
    { label: "Ninguno", text: "Desmarca todos." },
    { label: "Switch de cada archivo", text: "Lo mete o lo saca del ZIP." },
    { label: "Cancelar", text: "Cierra sin generar nada." },
    {
      label: "Generar (N archivos)",
      text: "Arma el ZIP con lo marcado y te da el enlace de descarga y la contraseña. Sale gris si no hay nada marcado.",
    },
    {
      label: "Copiar (al terminar)",
      text: "Copia el enlace o la contraseña. Para mandarlos, elige el paquete en «Paquete ZIP a incluir» al enviar el correo: el correo lleva los dos.",
    },
  );

  return {
    manual: "zip",
    intro: (
      <p>
        Junta evidencias del caso en un ZIP para mandarlas por correo. Al generarlo te da el
        enlace de descarga y, si lleva contraseña, la contraseña: los últimos 6 dígitos del TE.
      </p>
    ),
    secciones: [
      { titulo: "Qué hace cada botón", items: botones },
      {
        titulo: "Qué se marca solo al abrir",
        cuerpo: (
          <>
            <p>
              Si el caso tiene un solo IMEI, quedan marcadas las evidencias de ese equipo y las
              que no tienen equipo. Con varios IMEIs (o ninguno), si hay imágenes del mensaje al
              cliente se marcan solo esas; si no, todas.
            </p>
            <p>
              En todos los casos, las fotos de la bitácora y lo que mandó el proveedor se quedan
              sin marcar: son material interno. Si de verdad van, márcalas a mano.
            </p>
          </>
        ),
      },
      {
        titulo: "Qué significa cada ficha",
        items: [
          {
            label: <Ficha cls="text-accent-300 bg-accent-500/10 border-accent-500/30">📎 mensaje</Ficha>,
            text: "Imagen de más de 1 MB que se subió desde el editor de un correo; se guardó como evidencia.",
          },
          {
            label: <Ficha cls="text-amber-300 bg-amber-500/10 border-amber-500/30">📓 bitácora</Ficha>,
            text: "Foto de constancia que se subió en una nota de la bitácora (la captura de un WhatsApp, por ejemplo). Es material interno: no se marca sola.",
          },
          {
            label: <Ficha cls="text-sky-300 bg-sky-500/10 border-sky-500/30">📥 del proveedor</Ficha>,
            text: "Archivo que el proveedor mandó en su ticket. No se marca solo: devolvérselo no tiene caso.",
          },
          {
            label: "IMEI …",
            text: "La evidencia está asignada a ese equipo. Sin esa ficha, no tiene equipo.",
          },
          { label: "Fecha y hora", text: "Cuándo se cargó el archivo. Los más recientes van arriba." },
        ],
      },
    ],
  };
}
